/**
 * Shared Carousel Logic
 * Handles horizontal scrolling and navigation buttons for product/category/blog carousels.
 */

if (!window.NatureNestCarousel) {
    class NatureNestCarousel {
        constructor(container) {
            this.container = container;
            this.track = container.querySelector('[data-carousel-track]');
            this.prevBtn = container.querySelector('[data-carousel-prev]');
            this.nextBtn = container.querySelector('[data-carousel-next]');

            // Read config
            this.scrollDesktop = parseInt(container.dataset.scrollDesktop) || 300;
            this.scrollMobile = parseInt(container.dataset.scrollMobile) || parseInt(container.dataset.scrollAmount) || 300; // Fallback

            // New features
            this.autoplay = container.dataset.autoplay === 'true';
            this.pagination = container.dataset.pagination === 'true';
            this.autoplayInterval = parseInt(container.dataset.autoplayInterval) || 5000;
            this.autoplayTimer = null;

            if (!this.track) return; // Buttons are optional now

            this.init();
        }

        getScrollAmount() {
            return window.innerWidth > 768 ? this.scrollDesktop : this.scrollMobile;
        }

        init() {
            // Initial check
            if (this.prevBtn && this.nextBtn) this.updateButtons();

            // Setup Pagination
            if (this.pagination) {
                this.setupPagination();
            }

            // Setup Autoplay
            if (this.autoplay) {
                this.startAutoplay();
                // Pause on hover
                this.container.addEventListener('mouseenter', () => this.stopAutoplay());
                this.container.addEventListener('mouseleave', () => this.startAutoplay());
                // Pause on touch
                this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
                this.track.addEventListener('touchend', () => this.startAutoplay());
            }

            // Event Listeners
            this.track.addEventListener('scroll', () => {
                if (this.prevBtn && this.nextBtn) this.updateButtons();
                if (this.pagination) this.updatePagination();
            }, { passive: true });

            window.addEventListener('resize', () => {
                if (this.prevBtn && this.nextBtn) this.updateButtons();
                if (this.pagination) this.setupPagination(); // Re-calc dots
            });

            if (this.prevBtn) {
                this.prevBtn.addEventListener('click', () => {
                    this.track.scrollBy({ left: -this.getScrollAmount(), behavior: 'smooth' });
                });
            }

            if (this.nextBtn) {
                this.nextBtn.addEventListener('click', () => {
                    this.track.scrollBy({ left: this.getScrollAmount(), behavior: 'smooth' });
                });
            }
        }

        setupPagination() {
            // Remove existing pagination if any (for resize)
            const existingNav = this.container.querySelector('.carousel__pagination');
            if (existingNav) existingNav.remove();

            const items = this.track.children;
            if (items.length === 0) return;

            // Calculate how many "pages" or items we have
            // Simple approach: 1 dot per item for testimonials since they are large and distinct
            const dotCount = items.length;

            const nav = document.createElement('div');
            nav.className = 'carousel__pagination';

            for (let i = 0; i < dotCount; i++) {
                const dot = document.createElement('button');
                dot.className = 'carousel__dot';
                dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
                dot.addEventListener('click', () => {
                    const itemWidth = items[0].offsetWidth + 24; // Width + gap (approx)
                    // More precise: scroll to specific child offset
                    const targetLeft = items[i].offsetLeft - this.track.offsetLeft;
                    this.track.scrollTo({ left: targetLeft, behavior: 'smooth' });
                });
                nav.appendChild(dot);
            }

            this.container.appendChild(nav);
            this.updatePagination();
        }

        updatePagination() {
            const nav = this.container.querySelector('.carousel__pagination');
            if (!nav) return;

            const dots = nav.querySelectorAll('.carousel__dot');
            const items = this.track.children;
            const scrollCenter = this.track.scrollLeft + (this.track.clientWidth / 2);

            let activeIndex = 0;
            let minDist = Infinity;

            // Find which item is closest to center
            for (let i = 0; i < items.length; i++) {
                const itemCenter = items[i].offsetLeft - this.track.offsetLeft + (items[i].offsetWidth / 2);
                const dist = Math.abs(scrollCenter - itemCenter);
                if (dist < minDist) {
                    minDist = dist;
                    activeIndex = i;
                }
            }

            dots.forEach((dot, index) => {
                if (index === activeIndex) {
                    dot.classList.add('active');
                } else {
                    dot.classList.remove('active');
                }
            });
        }

        startAutoplay() {
            this.stopAutoplay();
            this.autoplayTimer = setInterval(() => {
                const maxScroll = this.track.scrollWidth - this.track.clientWidth;
                // If at end, scroll back to start
                if (this.track.scrollLeft >= maxScroll - 10) {
                    this.track.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    this.track.scrollBy({ left: this.getScrollAmount(), behavior: 'smooth' });
                }
            }, this.autoplayInterval);
        }

        stopAutoplay() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
        }

        updateButtons() {
            const tolerance = 10;
            const isAtStart = this.track.scrollLeft <= tolerance;
            const isAtEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - tolerance;

            if (this.prevBtn) {
                if (isAtStart) this._disableBtn(this.prevBtn);
                else this._enableBtn(this.prevBtn);
            }

            if (this.nextBtn) {
                if (isAtEnd) this._disableBtn(this.nextBtn);
                else this._enableBtn(this.nextBtn);
            }
        }

        _disableBtn(btn) {
            btn.style.opacity = '0.3';
            btn.style.pointerEvents = 'none';
            if (btn.classList.contains('product-carousel__nav-btn--prev') || btn.classList.contains('category-carousel__nav-btn--prev') || btn.classList.contains('testimonials__nav-btn--prev')) {
                btn.style.opacity = '0';
            }
            btn.disabled = true;
        }

        _enableBtn(btn) {
            btn.style.opacity = '1';
            btn.style.pointerEvents = 'auto';
            btn.disabled = false;
        }
    }

    // Expose to window for manual initialization
    window.NatureNestCarousel = NatureNestCarousel;

    // Auto-initialize
    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('[data-carousel-container]').forEach(container => {
            new NatureNestCarousel(container);
        });
    });
}
